import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Trophy, Star, Award } from 'lucide-react';
import { API, getLogoUrl } from '../config/api';
import { useLanguage } from '../contexts/LanguageContext';
import ShareButtons from '../components/ShareButtons';
import BracketMatch from '../components/BracketMatch';

const ScoreboardPage = () => {
  const [standings, setStandings] = useState([]);
  const [playoffGames, setPlayoffGames] = useState([]);
  const { t, language } = useLanguage();

  useEffect(() => {
    axios.get(`${API}/standings?season=1`).then(res => setStandings(res.data)).catch(console.error);
    axios.get(`${API}/games?season=1`).then(res => {
      const playoffs = res.data.filter(g => g.week >= 10);
      setPlayoffGames(playoffs);
    }).catch(console.error);
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-4 py-12" data-testid="scoreboard-page">
      <div className="text-center mb-12">
        <h1 className="section-title mb-4">{t('scoreboardTitle')}</h1>
        <p className="text-slate-600">{t('scoreboardSubtitle')}</p>
      </div>

      {/* Standings Table with Games Played */}
      <div className="card overflow-hidden" data-testid="standings-table">
        <div className="bg-[#1E3A5F] text-white p-4">
          <h2 className="font-display font-bold text-xl flex items-center"><Trophy className="mr-2" /> {t('leagueStandings')}</h2>
          <p className="text-white/70 text-sm mt-1">{language === 'gr' ? 'Κατάταξη Πρωταθλήματος 1ης Σεζόν' : '1st Season Championship Standings'}</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="table-header">
                <th className="p-4">#</th>
                <th className="p-4">{t('team')}</th>
                <th className="p-4 text-center">{language === 'gr' ? 'Β' : 'PTS'}</th>
                <th className="p-4 text-center">{t('wins')}</th>
                <th className="p-4 text-center">{t('losses')}</th>
                <th className="p-4 text-center">{t('gamesPlayed')}</th>
                <th className="p-4 text-center">{t('pointsFor')}</th>
                <th className="p-4 text-center">{t('pointsAgainst')}</th>
                <th className="p-4 text-center">{t('pointDiff')}</th>
              </tr>
            </thead>
            <tbody>
              {standings.map((team, i) => (
                <tr key={team.id} className="table-row" data-testid={`standing-row-${team.id}`}>
                  <td className="p-4 font-bold">{i + 1}</td>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      {team.has_logo ? (
                        <img src={getLogoUrl(team.id)} alt={team.name} className="w-8 h-8 rounded-full object-cover" />
                      ) : (
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: team.color }}>
                          {team.short_name}
                        </div>
                      )}
                      <span className="font-medium">{team.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-center font-bold text-[#F97316] text-lg">{team.score_points || 0}</td>
                  <td className="p-4 text-center font-bold text-green-600">{team.wins}</td>
                  <td className="p-4 text-center font-bold text-red-500">{team.losses}</td>
                  <td className="p-4 text-center font-semibold">{team.games_played}</td>
                  <td className="p-4 text-center font-semibold text-blue-600">{team.points_for}</td>
                  <td className="p-4 text-center font-semibold text-orange-600">{team.points_against}</td>
                  <td className={`p-4 text-center font-bold ${team.point_diff > 0 ? 'text-green-600' : team.point_diff < 0 ? 'text-red-500' : ''}`}>
                    {team.point_diff > 0 ? '+' : ''}{team.point_diff}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Playoff Brackets */}
      {playoffGames.length > 0 && (() => {
        const top6 = playoffGames.filter(g => g.phase === 'Top 6 Playoffs');
        const semis = playoffGames.filter(g => g.phase?.includes('Ημιτελικά'));
        const smallFinal = playoffGames.filter(g => g.phase?.includes('Μικρός'));
        const grandFinal = playoffGames.filter(g => g.phase?.includes('Μεγάλος'));
        const placement = playoffGames.filter(g => g.phase === 'Αγώνες Κατάταξης Θέσεων 7–9');
        const placementFinal = playoffGames.filter(g => g.phase?.includes('Τελικός Κατάταξης'));

        return (
          <>
            {/* Top 6 / Final 4 Bracket */}
            <div className="mt-10 card overflow-hidden" data-testid="playoff-bracket">
              <div className="bg-gradient-to-r from-[#F97316] to-[#EA580C] text-white p-4">
                <h2 className="font-display font-bold text-xl flex items-center">
                  <Trophy className="mr-2" /> {language === 'gr' ? 'Top 6 Playoffs & Final 4' : 'Top 6 Playoffs & Final 4'}
                </h2>
              </div>
              <div className="p-6 bg-slate-50">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-center">
                  
                  {/* Round 1: Top 6 Playoffs */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">
                      {language === 'gr' ? 'Top 6 Playoffs' : 'Top 6 Playoffs'}
                    </h3>
                    {[...top6].sort((a, b) => (a.time || '').localeCompare(b.time || '')).map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>

                  {/* Round 2: Semifinals */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">
                      {language === 'gr' ? 'Ημιτελικά' : 'Semifinals'}
                    </h3>
                    {semis.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>

                  {/* Round 3: Small Final */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">
                      {language === 'gr' ? 'Μικρός Τελικός' : 'Small Final'}
                    </h3>
                    {smallFinal.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>

                  {/* Round 4: Grand Final */}
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-[#F97316] uppercase tracking-wider text-center flex items-center justify-center gap-1">
                      <Award size={14} /> {language === 'gr' ? 'Μεγάλος Τελικός' : 'Grand Final'}
                    </h3>
                    {grandFinal.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                </div>
              </div>
            </div>

            {/* Placement 7-9 Bracket */}
            <div className="mt-8 card overflow-hidden" data-testid="placement-bracket">
              <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2D5A87] text-white p-4">
                <h2 className="font-display font-bold text-xl flex items-center">
                  <Star className="mr-2" /> {language === 'gr' ? 'Κατάταξη Θέσεων 7–9' : 'Placement 7–9'}
                </h2>
              </div>
              <div className="p-6 bg-slate-50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center max-w-2xl mx-auto">
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider text-center">
                      {language === 'gr' ? 'Αγώνας 8ης vs 9ης' : '8th vs 9th'}
                    </h3>
                    {placement.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xs font-bold text-[#F97316] uppercase tracking-wider text-center flex items-center justify-center gap-1">
                      <Award size={14} /> {language === 'gr' ? 'Τελικός Κατάταξης' : 'Placement Final'}
                    </h3>
                    {placementFinal.map(g => <BracketMatch key={g.id} game={g} language={language} />)}
                  </div>
                </div>
              </div>
            </div>
          </>
        );
      })()}

      <div className="mt-4 flex justify-end">
        <ShareButtons title={language === 'gr' ? 'Βαθμολογία WBLA' : 'WBLA Standings'} />
      </div>
    </div>
  );
};

export default ScoreboardPage;
